import React from "react";
import { Link } from "react-router-dom";
import { Package, ArrowRight } from "lucide-react";

const STATUS_LABELS = {
  pending:    "Pending",
  quoted:     "Quoted",
  confirmed:  "Confirmed",
  processing: "Processing",
  packing:    "Packing",
  shipped:    "Shipped",
  delivered:  "Delivered",
  completed:  "Completed",
  cancelled:  "Cancelled",
};

const STATUS_TONE = {
  pending:    "bg-amber-100 text-amber-900",
  quoted:     "bg-blue-100 text-blue-900",
  confirmed:  "bg-blue-100 text-blue-900",
  processing: "bg-indigo-100 text-indigo-900",
  packing:    "bg-indigo-100 text-indigo-900",
  shipped:    "bg-emerald-100 text-emerald-900",
  delivered:  "bg-emerald-100 text-emerald-900",
  completed:  "bg-emerald-100 text-emerald-900",
  cancelled:  "bg-neutral-200 text-neutral-800",
};

/**
 * CompactOrderCard — orders are secondary on the collection-first
 * dashboard. This card surfaces just enough to reassure the collector
 * their in-flight order is being handled, and links them out to the
 * full My Orders page. Zero-state included.
 */
export default function CompactOrderCard({ orders }) {
  const latest = orders?.latest;
  const activeCount = Number(orders?.active_count || 0);
  const totalCount = Number(orders?.total_count || 0);
  if (!latest && totalCount === 0) {
    return (
      <div
        className="rounded-2xl border-2 border-[var(--ink)] bg-white p-5 md:p-6 shadow-[4px_4px_0_var(--ink)]"
        data-testid="dash-orders-empty"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center shrink-0">
              <Package size={20} aria-hidden />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg md:text-xl leading-tight">No orders yet</div>
              <div className="text-sm text-[var(--ink)]/70 mt-0.5">
                Missing stickers? Get a quote and let Gil help complete your order.
              </div>
            </div>
          </div>
          <Link
            to="/order-missing"
            data-testid="dash-orders-cta-empty"
            aria-label="Order missing stickers"
            className="inline-flex items-center justify-center gap-1 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2.5 text-xs font-black uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)] whitespace-nowrap"
          >
            Order Missing Stickers
          </Link>
        </div>
      </div>
    );
  }
  const tone = STATUS_TONE[latest?.status] || "bg-neutral-100 text-neutral-900";
  const statusLabel = STATUS_LABELS[latest?.status] || (latest?.status || "").toString();
  return (
    <div
      className="rounded-2xl border-2 border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--ink)]"
      data-testid="dash-orders-card"
    >
      <div className="p-5 md:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-lg bg-[var(--panini-yellow-soft)] border-2 border-[var(--ink)] grid place-items-center shrink-0">
            <Package size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[var(--smoke)] font-bold">
              Latest order
            </div>
            <div className="font-display text-lg truncate" data-testid="dash-orders-latest-id">
              #{latest.public_id}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-bold uppercase tracking-widest ${tone}`} data-testid="dash-orders-latest-status">
                {statusLabel}
              </span>
              {typeof latest.sticker_count === "number" && (
                <span className="text-[var(--smoke)]">{latest.sticker_count} stickers</span>
              )}
              {typeof latest.total === "number" && (
                <span className="font-mono tabular-nums text-[var(--smoke)]">
                  ${Number(latest.total).toFixed(2)}
                </span>
              )}
            </div>
          </div>
        </div>
        <Link
          to="/dashboard/orders"
          data-testid="dash-orders-cta"
          aria-label="View all orders"
          className="inline-flex items-center gap-1 rounded-full border-2 border-[var(--ink)] bg-white px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)] whitespace-nowrap"
        >
          View all orders <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
      {activeCount > 1 && (
        <div className="px-5 md:px-6 py-2 border-t-2 border-[var(--ink)] bg-[var(--panini-yellow-soft)]/40 text-xs text-[var(--smoke)]">
          You have <span className="font-bold text-[var(--ink)]">{activeCount}</span> active orders in progress.
        </div>
      )}
    </div>
  );
}
